"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { AuthCtaLink } from "./auth-cta-link";
import type { NavbarConfig } from "@/config/counties/types";
import { trackFbLead } from "@/lib/analytics/facebook-pixel";

type NavbarProps = {
  data: NavbarConfig;
  basePath?: string;
};

export default function Navbar({ data, basePath = "/" }: NavbarProps) {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-line-soft h-16 min-h-16 py-2 sm:py-4 flex items-center">
      <div className="container mx-auto h-full px-4 sm:px-6 md:px-[40px] flex items-center justify-between gap-3">
        <Link href={basePath} className="flex items-center shrink-0">
          <Image
            src="/full-logo.svg"
            alt="LeadsMiner"
            width={195}
            height={30}
            className="h-6 sm:h-8 w-auto"
            priority
          />
        </Link>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          <a href="/#features" className="text-base font-medium text-muted-ink hover:text-primary transition-colors">
            Features
          </a>
          <a href="/#process" className="text-base font-medium text-muted-ink hover:text-primary transition-colors">
            How It Works
          </a>
          {/* A real route, not an on-page anchor: /pricing is now a full page.
              Link (not <a>) so it client-side navigates like the other
              cross-page items below. */}
          <Link href="/pricing" className="text-base font-medium text-muted-ink hover:text-primary transition-colors">
            Pricing
          </Link>
          <a href="/#faq" className="text-base font-medium text-muted-ink hover:text-primary transition-colors">
            FAQ
          </a>
          {/* Cross-page route (not an on-page anchor): the data-honesty policy
              was previously reachable only from the footer. */}
          <Link href="/data-honesty" className="text-base font-medium text-muted-ink hover:text-primary transition-colors">
            Data Honesty
          </Link>
          <Link href="/blog" className="text-base font-medium text-muted-ink hover:text-primary transition-colors">
            Blog
          </Link>
          <Link href="/contact" className="text-base font-medium text-muted-ink hover:text-primary transition-colors">
            Contact
          </Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0">
          <AuthCtaLink
            href="/dashboard"
            className="group btn-ripple press inline-flex items-center justify-center h-[44px] px-4 sm:px-6 rounded-[6px] text-[14px] lg:text-[16px] font-medium text-white whitespace-nowrap bg-primary-surface transition-all gap-2"
            onClick={() => trackFbLead()}
          >
            <span className="sm:hidden">{data.ctaTextMobile}</span>
            <span className="hidden sm:inline">{data.ctaTextDesktop}</span>
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </AuthCtaLink>
        </div>
      </div>
    </header>
  );
}
